import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Settings as SettingsIcon,
  Mail,
  Loader2,
  Check,
  Link2Off,
  RefreshCw,
  Disc,
  ShoppingCart,
  ExternalLink,
} from 'lucide-react';
import { api } from '../services/api';
import { useToast } from '../components/common/Toast';

// Gmail logo component
function GmailLogo({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none">
      <path d="M22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6z" fill="#EA4335"/>
      <path d="M22 6l-10 7L2 6" stroke="#fff" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M2 6l10 7 10-7" fill="#FBBC05" opacity="0.8"/>
      <path d="M2 18V6l10 7" fill="#34A853" opacity="0.8"/>
      <path d="M22 18V6l-10 7" fill="#4285F4" opacity="0.8"/>
    </svg>
  );
}

// Outlook logo component
function OutlookLogo({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none">
      <rect x="2" y="4" width="20" height="16" rx="2" fill="#0078D4"/>
      <ellipse cx="9" cy="12" rx="4" ry="5" fill="#fff"/>
      <ellipse cx="9" cy="12" rx="2.5" ry="3" fill="#0078D4"/>
      <path d="M15 8h5v2h-5V8zm0 3h5v2h-5v-2zm0 3h4v2h-4v-2z" fill="#fff" opacity="0.8"/>
    </svg>
  );
}

interface EmailAccountStatus {
  configured: boolean;
  connected: boolean;
  email?: string;
  lastSyncAt?: string | null;
  emailCount?: number;
  linkedCount?: number;
}

// Format relative time
const formatRelativeTime = (dateStr: string | null | undefined) => {
  if (!dateStr) return 'Never';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  return date.toLocaleDateString();
};

export function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  // Handle OAuth callback params
  const gmailConnected = searchParams.get('gmail_connected');
  const gmailError = searchParams.get('gmail_error');
  const gmailEmail = searchParams.get('email');
  const outlookConnected = searchParams.get('outlook_connected');
  const outlookError = searchParams.get('outlook_error');

  // Show toasts for OAuth callbacks
  useEffect(() => {
    if (gmailConnected) {
      showToast({ message: `Gmail connected: ${gmailEmail}`, type: 'success' });
      queryClient.invalidateQueries({ queryKey: ['gmail-status'] });
    }
    if (gmailError) {
      showToast({ message: `Gmail connection failed: ${gmailError}`, type: 'error' });
    }
    if (outlookConnected) {
      showToast({ message: 'Outlook connected successfully', type: 'success' });
      queryClient.invalidateQueries({ queryKey: ['outlook-status'] });
    }
    if (outlookError) {
      showToast({ message: `Outlook connection failed: ${outlookError}`, type: 'error' });
    }

    // Clear OAuth params from URL
    if (gmailConnected || gmailError || outlookConnected || outlookError) {
      const timer = setTimeout(() => {
        setSearchParams(new URLSearchParams());
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [gmailConnected, gmailError, gmailEmail, outlookConnected, outlookError, setSearchParams, showToast, queryClient]);

  // Fetch Gmail status
  const { data: gmailStatus, isLoading: loadingGmail } = useQuery<EmailAccountStatus>({
    queryKey: ['gmail-status'],
    queryFn: () => api.email.getGmailStatus(),
  });

  // Fetch Outlook status
  const { data: outlookStatus, isLoading: loadingOutlook } = useQuery<EmailAccountStatus>({
    queryKey: ['outlook-status'],
    queryFn: () => api.email.getOutlookStatus(),
  });

  // Fetch marketplace statuses
  const { data: discogsStatus } = useQuery({
    queryKey: ['discogs-status'],
    queryFn: () => api.discogs.getStatus(),
  });

  const { data: ebayStatus } = useQuery({
    queryKey: ['ebay-status'],
    queryFn: () => api.ebay.getStatus(),
  });

  // Gmail mutations
  const gmailConnectMutation = useMutation({
    mutationFn: () => api.email.getGmailAuthUrl(),
    onSuccess: (data) => {
      window.location.href = data.authUrl;
    },
    onError: (error: Error) => {
      showToast({ message: `Failed to connect Gmail: ${error.message}`, type: 'error' });
    },
  });

  const gmailDisconnectMutation = useMutation({
    mutationFn: () => api.email.disconnectGmail(),
    onSuccess: () => {
      showToast({ message: 'Gmail disconnected', type: 'success' });
      queryClient.invalidateQueries({ queryKey: ['gmail-status'] });
    },
  });

  const gmailSyncMutation = useMutation({
    mutationFn: () => api.email.syncGmail(),
    onSuccess: (data) => {
      showToast({ message: `Synced ${data.synced} emails, ${data.linked} linked to items`, type: 'success' });
      queryClient.invalidateQueries({ queryKey: ['gmail-status'] });
    },
    onError: (error: Error) => {
      showToast({ message: `Sync failed: ${error.message}`, type: 'error' });
    },
  });

  // Outlook mutations
  const outlookConnectMutation = useMutation({
    mutationFn: () => api.email.getOutlookAuthUrl(),
    onSuccess: (data) => {
      window.location.href = data.authUrl;
    },
    onError: (error: Error) => {
      showToast({ message: `Failed to connect Outlook: ${error.message}`, type: 'error' });
    },
  });

  const outlookDisconnectMutation = useMutation({
    mutationFn: () => api.email.disconnectOutlook(),
    onSuccess: () => {
      showToast({ message: 'Outlook disconnected', type: 'success' });
      queryClient.invalidateQueries({ queryKey: ['outlook-status'] });
    },
  });

  const outlookSyncMutation = useMutation({
    mutationFn: () => api.email.syncOutlook(),
    onSuccess: (data) => {
      showToast({ message: `Synced ${data.synced} emails, ${data.linked} linked to items`, type: 'success' });
      queryClient.invalidateQueries({ queryKey: ['outlook-status'] });
    },
    onError: (error: Error) => {
      showToast({ message: `Sync failed: ${error.message}`, type: 'error' });
    },
  });

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 bg-gradient-to-br from-gray-500 to-gray-600 rounded-xl">
          <SettingsIcon className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600">Manage your email and marketplace connections</p>
        </div>
      </div>

      {/* Email Connections Section */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Mail className="w-5 h-5" />
          Email Connections
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Connect your email accounts to automatically track Discogs notification emails and link them to your sold items.
        </p>

        <div className="grid gap-4 md:grid-cols-2">
          {/* Gmail Card */}
          <div className="card p-5">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-red-50 rounded-xl">
                <GmailLogo className="w-8 h-8" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900">Gmail</h3>
                {loadingGmail ? (
                  <div className="flex items-center gap-2 mt-2">
                    <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                    <span className="text-sm text-gray-500">Loading...</span>
                  </div>
                ) : !gmailStatus?.configured ? (
                  <p className="text-sm text-gray-500 mt-1">Not configured</p>
                ) : gmailStatus.connected ? (
                  <>
                    <p className="text-sm text-green-600 flex items-center gap-1 mt-1">
                      <Check className="w-4 h-4" />
                      {gmailStatus.email}
                    </p>
                    <div className="text-xs text-gray-500 mt-2 space-y-1">
                      <p>Last synced: {formatRelativeTime(gmailStatus.lastSyncAt)}</p>
                      <p>{gmailStatus.emailCount || 0} emails synced, {gmailStatus.linkedCount || 0} linked to items</p>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-gray-500 mt-1">Not connected</p>
                )}
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              {gmailStatus?.connected ? (
                <>
                  <button
                    onClick={() => gmailSyncMutation.mutate()}
                    disabled={gmailSyncMutation.isPending}
                    className="flex-1 btn-secondary text-sm py-2"
                  >
                    {gmailSyncMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-1.5" />
                        Sync Now
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      if (confirm('Disconnect Gmail? Your synced emails will be preserved.')) {
                        gmailDisconnectMutation.mutate();
                      }
                    }}
                    disabled={gmailDisconnectMutation.isPending}
                    className="btn-secondary text-sm py-2 text-red-600 hover:bg-red-50"
                  >
                    <Link2Off className="w-4 h-4" />
                  </button>
                </>
              ) : gmailStatus?.configured ? (
                <button
                  onClick={() => gmailConnectMutation.mutate()}
                  disabled={gmailConnectMutation.isPending}
                  className="flex-1 btn-primary text-sm py-2"
                >
                  {gmailConnectMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                  ) : (
                    'Connect Gmail'
                  )}
                </button>
              ) : (
                <p className="text-xs text-gray-400 italic">Gmail integration not configured on server</p>
              )}
            </div>
          </div>

          {/* Outlook Card */}
          <div className="card p-5">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-50 rounded-xl">
                <OutlookLogo className="w-8 h-8" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900">Outlook</h3>
                {loadingOutlook ? (
                  <div className="flex items-center gap-2 mt-2">
                    <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                    <span className="text-sm text-gray-500">Loading...</span>
                  </div>
                ) : !outlookStatus?.configured ? (
                  <p className="text-sm text-gray-500 mt-1">Not configured</p>
                ) : outlookStatus.connected ? (
                  <>
                    <p className="text-sm text-green-600 flex items-center gap-1 mt-1">
                      <Check className="w-4 h-4" />
                      {outlookStatus.email}
                    </p>
                    <div className="text-xs text-gray-500 mt-2 space-y-1">
                      <p>Last synced: {formatRelativeTime(outlookStatus.lastSyncAt)}</p>
                      <p>{outlookStatus.emailCount || 0} emails synced, {outlookStatus.linkedCount || 0} linked to items</p>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-gray-500 mt-1">Not connected</p>
                )}
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              {outlookStatus?.connected ? (
                <>
                  <button
                    onClick={() => outlookSyncMutation.mutate()}
                    disabled={outlookSyncMutation.isPending}
                    className="flex-1 btn-secondary text-sm py-2"
                  >
                    {outlookSyncMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-1.5" />
                        Sync Now
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      if (confirm('Disconnect Outlook? Your synced emails will be preserved.')) {
                        outlookDisconnectMutation.mutate();
                      }
                    }}
                    disabled={outlookDisconnectMutation.isPending}
                    className="btn-secondary text-sm py-2 text-red-600 hover:bg-red-50"
                  >
                    <Link2Off className="w-4 h-4" />
                  </button>
                </>
              ) : outlookStatus?.configured ? (
                <button
                  onClick={() => outlookConnectMutation.mutate()}
                  disabled={outlookConnectMutation.isPending}
                  className="flex-1 btn-primary text-sm py-2"
                >
                  {outlookConnectMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                  ) : (
                    'Connect Outlook'
                  )}
                </button>
              ) : (
                <p className="text-xs text-gray-400 italic">Outlook integration not configured on server</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Marketplace Connections Section */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <ShoppingCart className="w-5 h-5" />
          Marketplace Connections
        </h2>

        <div className="grid gap-4 md:grid-cols-2">
          {/* Discogs Card */}
          <div className="card p-5">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-orange-50 rounded-xl">
                <Disc className="w-8 h-8 text-orange-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900">Discogs</h3>
                {discogsStatus?.connected ? (
                  <>
                    <p className="text-sm text-green-600 flex items-center gap-1 mt-1">
                      <Check className="w-4 h-4" />
                      Connected as {discogsStatus.username}
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-gray-500 mt-1">Not connected</p>
                )}
              </div>
            </div>

            <div className="mt-4">
              <a
                href="/selling"
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                Manage in Selling page
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>

          {/* eBay Card */}
          <div className="card p-5">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-yellow-50 rounded-xl">
                <ShoppingCart className="w-8 h-8 text-yellow-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900">eBay</h3>
                {ebayStatus?.connected ? (
                  <>
                    <p className="text-sm text-green-600 flex items-center gap-1 mt-1">
                      <Check className="w-4 h-4" />
                      Connected ({ebayStatus.siteId})
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-gray-500 mt-1">Not connected</p>
                )}
              </div>
            </div>

            <div className="mt-4">
              <a
                href="/selling"
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                Manage in Selling page
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
